import React from 'react';
import SEO from '../components/SEO';
import BackgroundDesign from '../components/BackgroundDesign';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  padding: 2rem 0;
  margin-top: 80px;
  background: ${props => props.theme.colors.background};
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageHero = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1)),
              url(/images/photo_5802988364859820042_y.jpg) center/cover no-repeat;
    filter: brightness(0.3);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, ${props => props.theme.colors.background}, transparent);
    z-index: 2;
  }
`;

const PageHeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
  animation: ${fadeUp} 1s ease-out;
`;

const PageTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  span {
    color: ${props => props.theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  opacity: 0.9;
  margin-bottom: 2rem;
`;

const EventsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EventCard = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  height: 400px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.8));
    z-index: 1;
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }

  &:hover::before {
    background: linear-gradient(to bottom, rgba(153,0,17,0.4), rgba(0,0,0,0.9));
  }
`;

const EventImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  height: 100%;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  }
`;

const DateBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem;
  border-radius: 13px;
  font-weight: 700;
  z-index: 2;
  text-align: center;
  min-width: 100px;
  
  span {
    display: block;
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
  }
`;

const EventContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem;
  padding-right: 4rem;
  color: white;
  z-index: 2;
`;

const EventTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: white;
  font-weight: 700;
`;

const ViewDetailsButton = styled.button`
  background: transparent;
  border: 2px solid ${props => props.theme.colors.primary};
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  text-align: center;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  color: ${props => props.theme.colors.text};
  font-size: 2.5rem;
  margin-bottom: 3rem;
  font-weight: 700;
  opacity: 0;
  animation: ${fadeUp} 0.8s ease-out forwards;
  animation-delay: 0.3s;
  
  span {
    color: ${props => props.theme.colors.primary};
  }
`;

const events = [
  {
    id: 1,
    title: "The Big Heart: 3 Years of Impact",
    date: "23th FEB",
    time: "2:00 pm - 7:00 pm",
    location: "Casablanca, Morocco",
    image: `${process.env.PUBLIC_URL}/images/EventPage/Birthday-event.jpg`,
    slug: "3-years-impact",
    description: "A celebration of three extraordinary years of compassion, dedication, and transformation...",
    fullDescription: `"The Big Heart: 3 Years of Impact" marks a special celebration of three extraordinary years of compassion, dedication, and transformation. This day is a reflection on the progress we've made and a tribute to the remarkable members who have driven our mission forward with their love and commitment. These biggest hearts have worked tirelessly to bring hope, support, and change to communities across Morocco, touching countless lives and making a real difference.`
  },
  {
    id: 2,
    title: "The Big Heart at the International Plastics Forum",
    date: "26th JUN",
    time: "9:00 am - 4:00 pm",
    location: "Casablanca, Morocco",
    image: `${process.env.PUBLIC_URL}/images/EventPage/event-image.jpg`,
    slug: "plastics-forum",
    description: "Join us at the International Plastics Forum where innovation meets social impact...",
    fullDescription: `The Big Heart was honored to participate in the International Plastics Forum, held in Casablanca from June 26 to 28, 2024. This prestigious event brought together innovators and leaders from around the world to discuss the future of the plastics industry, sustainability, and social impact.`
  },
  {
    id: 3,
    title: "The Big Heart Goes Global: Next Stop, Portugal!",
    date: "06th OCT",
    time: "9:00 am - 6:00 pm",
    location: "Lisbon, Portugal",
    image: `${process.env.PUBLIC_URL}/images/sliders/imgi_309_PSGE_3.webp`,
    slug: "portugal-event",
    description: "Join us at the International Plastics Forum where innovation meets social impact...",
    fullDescription: `The Big Heart was honored to participate in the International Plastics Forum, held in Casablanca from June 26 to 28, 2024. This prestigious event brought together innovators and leaders from around the world to discuss the future of the plastics industry, sustainability, and social impact.`
  },
  {
    id: 4,
    title: "The Big Heart au Marché de Printemps",
    date: "14th JUN",
    time: "morning to evening",
    location: "Triangle Vert Bouskoura",
    image: `${process.env.PUBLIC_URL}/images/EventPage/marche-du-printemps/printemps-5.jpeg`,
    slug: "marche-de-printemps",
    description: "Notre participation au Marché de Printemps autour du partage, de la solidarité et de l'engagement citoyen.",
    fullDescription: `Nous sommes heureux d’annoncer notre participation en tant que partenaire au Marché de Printemps, qui se tiendra ce dimanche 14 juin 2026 au Triangle Vert Bouskoura.

À travers ce partenariat, The Big Heart s’inscrit dans une dynamique de partage, de solidarité et d’engagement citoyen. Cet événement familial met à l’honneur la créativité des enfants et des jeunes, tout en créant un véritable espace de rencontre autour de valeurs humaines fortes.

Lors de cette journée, nous aurons le plaisir de présenter notre projet humanitaire « Heartbeat Journey », d’échanger avec la communauté dans un environnement chaleureux et convivial, et de sensibiliser à nos initiatives en faveur des populations vulnérables.

Parce que chaque geste compte, nous croyons que ces moments de proximité permettent de renforcer les liens et de construire ensemble un avenir plus solidaire.

✨ Nous vous donnons rendez-vous ce dimanche pour partager ce moment avec nous ! Venez nombreux nous rencontrer et soutenir cette belle initiative.`
  }
];

const EventsPage = () => {
  return (
    <PageWrapper>
      <BackgroundDesign />
      <SEO
        title="Events | The Big Heart Association Morocco | Community Initiatives"
        description="Join The Big Heart Association's events in Morocco and internationally. From the Plastics Summit in Lisbon to community celebrations in Casablanca, discover how we create impact."
        url="/events"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Events", url: "/events" }]}
        noOrganizationSchema
      />
      <PageHero>
        <PageHeroContent>
          <PageTitle>Our <span>Events</span></PageTitle>
          <PageSubtitle>Join us in making a difference. Together, we can create lasting impact through our community events and initiatives.</PageSubtitle>
        </PageHeroContent>
      </PageHero>
      <MainContent>
        <SectionTitle>Events Of Our <span>Association</span></SectionTitle>
        <EventsGrid>
          {events.map(event => (
            <EventCard key={event.id}>
              <EventImage image={event.image}>
                <DateBadge>
                  <span>{event.date.split(' ')[0]}</span>
                  {event.date.split(' ')[1]}
                </DateBadge>
              </EventImage>
              <EventContent>
                <EventTitle>{event.title}</EventTitle>
                {/* <EventDetails>
                  <div>
                    <FontAwesomeIcon icon={faClock} />
                    {event.time}
                  </div>
                  <div>
                    <FontAwesomeIcon icon={faLocationDot} />
                    {event.location}
                  </div>
                </EventDetails>
                <EventDescription>{event.description}</EventDescription> */}
                <ViewDetailsButton as={Link} to={`/events/${event.slug}`}>
                  View Details
                </ViewDetailsButton>
              </EventContent>
            </EventCard>
          ))}
        </EventsGrid>
      </MainContent>
    </PageWrapper>
  );
};

export default EventsPage;